import React, { useState, useEffect, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Send, X, ExternalLink, Sparkles, Loader2, BookOpen, User, Bot } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

// Componentes internos para melhor performance e legibilidade
const KarlTab = React.memo(({ messages, loading, input, setInput, sendMessage, handleKeyPress, messagesEndRef, currentModuleId }) => (
    <>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
                <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                    <div
                        className={`max-w-[85%] p-3 rounded-none text-sm ${msg.role === 'user'
                            ? 'bg-[#1A1A1A] text-white'
                            : 'bg-muted'
                            }`}
                    >
                        {msg.role === 'assistant' ? (
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </div>
                        ) : (
                            msg.content
                        )}
                    </div>
                </div>
            ))}
            {loading && (
                <div className="flex justify-start">
                    <div className="bg-muted p-3 rounded-none flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Karl digitando...</span>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>

        <div className="p-3 border-t border-border">
            <div className="flex gap-2">
                <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={currentModuleId ? "Dúvida sobre este módulo..." : "Pergunte ao Karl..."}
                    className="flex-1 rounded-none text-sm"
                    disabled={loading}
                />
                <Button
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className="rounded-none bg-white text-black hover:bg-neutral-200"
                    size="sm"
                >
                    <Send className="h-4 w-4" />
                </Button>
            </div>
            {currentModuleId && (
                <p className="text-[10px] text-center text-muted-foreground mt-1">
                    Contexto ativo: Módulo {currentModuleId}
                </p>
            )}
        </div>
    </>
));

const SuperNerdTab = React.memo(() => (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 rounded-full bg-[#E8DFD5] flex items-center justify-center mb-6 text-4xl">
            🤓
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Super Nerd
        </h3>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            Nosso agente pesquisador com acesso a <strong>todas as pesquisas</strong> e conteúdos curados da AIFT.
        </p>
        <div className="space-y-3 w-full max-w-xs">
            <a
                href={'https://gemini.google.com/gem/1GhK3GI6cviUQEEL95K7DuyuSq-0bog9E?usp=sharing'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]/90 transition-colors font-medium text-sm uppercase tracking-wide"
            >
                <ExternalLink className="h-4 w-4" />
                Acessar Super Nerd
            </a>
            <p className="text-xs text-muted-foreground">
                Powered by Google Gemini
            </p>
        </div>
    </div>
));

const MentoresTab = React.memo(({ instrutorMessage, setInstrutorMessage, sendToInstructor, sendingToInstructor }) => (
    <div className="flex-1 flex flex-col p-4">
        <div className="text-center mb-4">
            <div className="w-16 h-16 rounded-full bg-[#E8DFD5] flex items-center justify-center mx-auto mb-3">
                <User className="h-8 w-8 text-[#1A1A1A]" />
            </div>
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Fale com os Mentores
            </h3>
            <p className="text-sm text-muted-foreground">
                Envie uma mensagem direta para csouzageraldo@gmail.com
            </p>
        </div>
        <div className="flex-1 flex flex-col">
            <Textarea
                value={instrutorMessage}
                onChange={(e) => setInstrutorMessage(e.target.value)}
                placeholder="Escreva sua mensagem para os mentores..."
                className="flex-1 rounded-none resize-none text-sm min-h-[150px]"
                disabled={sendingToInstructor}
            />
            <Button
                onClick={sendToInstructor}
                disabled={sendingToInstructor || !instrutorMessage.trim()}
                className="mt-3 rounded-none bg-white text-black hover:bg-neutral-200 w-full font-bold"
            >
                {sendingToInstructor ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando...</>
                ) : (
                    <><Send className="h-4 w-4 mr-2" /> Enviar Mensagem</>
                )}
            </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-3">
            Responderemos o mais breve possível.
        </p>
    </div>
));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const NOTEBOOKLM_URL = 'https://notebooklm.google.com/notebook/6f7942f6-facb-485f-809e-753dd681e5b0/preview';

export default function AIAssistant() {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('karl'); // 'karl', 'base', 'instrutor'
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [instrutorMessage, setInstrutorMessage] = useState('');
    const [sendingToInstructor, setSendingToInstructor] = useState(false);
    const messagesEndRef = useRef(null);

    // Detectar Módulo Atual baseado na URL
    const moduleMatch = matchPath("/modules/:moduleId", location.pathname);
    const currentModuleId = moduleMatch?.params?.moduleId || null;

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Mensagem inicial do Karl
    useEffect(() => {
        if (isOpen && activeTab === 'karl' && messages.length === 0) {
            const initialMsg = currentModuleId
                ? `Olá! 👋 Sou o **Karl**. Vejo que você está no módulo atual. Como posso te ajudar com este conteúdo específico?`
                : `Olá! 👋 Sou o **Karl**, seu assistente IA do programa AIFT.
                
Posso ajudar você com:
- 📚 Dúvidas sobre os módulos
- 🤖 Explicações sobre IA
- 🛠️ Ferramentas
- 📋 Seu plano de transformação

Como posso ajudar hoje?`;

            setMessages([{
                role: 'assistant',
                content: initialMsg
            }]);
        }
    }, [isOpen, activeTab, messages.length, currentModuleId]);

    const sendMessage = async () => {
        if (!input.trim() || loading) return;

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setLoading(true);

        try {
            const response = await axios.post(`${API}/ai/chat`, {
                message: userMessage,
                module_id: currentModuleId
            }, { withCredentials: true });

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response.data.response
            }]);
        } catch (error) {
            console.error('Chat error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.'
            }]);
        } finally {
            setLoading(false);
        }
    };

    const sendToInstructor = async () => {
        if (!instrutorMessage.trim() || sendingToInstructor) return;

        setSendingToInstructor(true);
        try {
            // Endpoint correto: /chat/contact-admin
            await axios.post(`${API}/chat/contact-admin`, {
                subject: `Contato via Chat - ${currentModuleId ? 'Módulo ' + currentModuleId : 'Geral'}`,
                message: instrutorMessage
            }, { withCredentials: true });

            toast.success('Mensagem enviada ao instrutor!');
            setInstrutorMessage('');
        } catch (error) {
            console.error('Error sending to instructor:', error);
            toast.error('Erro ao enviar mensagem');
        } finally {
            setSendingToInstructor(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const tabs = [
        { id: 'karl', label: 'Karl', icon: Bot, desc: 'Dúvidas IA' },
        { id: 'super_nerd', label: 'Super Nerd', icon: BookOpen, desc: 'Pesquisador' },
        { id: 'mentores', label: 'Mentores', icon: User, desc: 'Suporte' },
    ];

    return (
        <>
            {/* Botão flutuante com avatar do Karl */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-lg hover:scale-105 transition-all flex items-center justify-center overflow-hidden border-2 border-[#E8DFD5]"
                data-testid="ai-assistant-toggle"
            >
                {isOpen ? (
                    <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                        <X className="h-6 w-6 text-white" />
                    </div>
                ) : (
                    <img
                        src="/karl_avatar.png"
                        alt="Karl - Assistente IA"
                        className="w-full h-full object-cover"
                    />
                )}
            </button>

            {/* Chat window */}
            {isOpen && (
                <Card className="fixed bottom-24 right-6 z-50 w-[400px] h-[550px] max-h-[80vh] rounded-none border border-border shadow-2xl flex flex-col overflow-hidden bg-background">
                    {/* Header */}
                    <div className="p-3 border-b border-border bg-[#1A1A1A] text-white flex items-center justify-center relative">
                        <h3 className="font-bold text-sm">AIFT Team</h3>
                        <button onClick={() => setIsOpen(false)} className="absolute right-3 p-1 hover:bg-white/10 rounded">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Chat Content - Only render active tab content when open */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                        <div className="flex border-b border-border">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 py-2 px-2 text-center transition-colors ${activeTab === tab.id
                                        ? 'bg-[#E8DFD5]/30 border-b-2 border-[#1A1A1A]'
                                        : 'hover:bg-muted'
                                        }`}
                                >
                                    {tab.id === 'karl' ? (
                                        <div className="w-5 h-5 mx-auto mb-1 rounded-full overflow-hidden">
                                            <img src="/karl_avatar.png" alt="Karl" className="w-full h-full object-cover" />
                                        </div>
                                    ) : (
                                        <tab.icon className="h-4 w-4 mx-auto mb-1" />
                                    )}
                                    <div className="text-xs font-medium">{tab.label}</div>
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-hidden flex flex-col">
                            {activeTab === 'karl' && <KarlTab
                                messages={messages}
                                loading={loading}
                                input={input}
                                setInput={setInput}
                                sendMessage={sendMessage}
                                handleKeyPress={handleKeyPress}
                                messagesEndRef={messagesEndRef}
                                currentModuleId={currentModuleId}
                            />}
                            {activeTab === 'super_nerd' && <SuperNerdTab />}
                            {activeTab === 'mentores' && <MentoresTab
                                instrutorMessage={instrutorMessage}
                                setInstrutorMessage={setInstrutorMessage}
                                sendToInstructor={sendToInstructor}
                                sendingToInstructor={sendingToInstructor}
                            />}
                        </div>
                    </div>
                </Card >
            )
            }
        </>
    );
}
